/**
 * FikoParcaTakipDialog — 🌌 (Fiko3DComponent) içinde MEVCUT dialog core ile açılan PROFESYONEL Parça Takip paneli.
 * STP gerçek motorda yüklü → fingerprint ile tanı/soru → aşama akışı + ölçü/ağırlık + HAREKETLER (ekle/sil/düzenle) + 3D tıkla-not.
 * Veri sunucuda (paylaşım/hareketler). FikoDialogService.ac({ bilesen: FikoParcaTakipDialog, parametreler: {...} }).
 */
import { Component, Input, ChangeDetectorRef, ChangeDetectionStrategy, NgZone, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { IKart } from '@service/fikoyt-ui/interfaces/IKart.interface';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { ParcaTakipService } from './services/parca-takip.service';
import { ASAMA_BILGI, PARCA_ASAMALAR, ALT_TIP_BILGI, ParcaAsama, ParcaAltTip, IParcaVeri, IParcaFingerprint } from './interfaces/parca-takip.interface';

@Component({
  selector: 'fiko-parca-takip-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <div class="pd">
    @if (fotoBuyuk()) {
      <div class="pd-foto-katman" (click)="fotoBuyuk.set('')"><img [src]="fotoBuyuk()" alt="foto"/></div>
    }
    @if (onMalzemeler?.length) {
      <datalist id="pd-malz-list">@for (m of onMalzemeler; track m.ad) { <option [value]="m.ad">{{ m.yogunluk }} g/cm³</option> }</datalist>
    }

    <!-- ═══ SORU (yeni parça / tanındı) ═══ -->
    @if (!parca()) {
      <div class="pd-soru">
        @if (taninanKart()) {
          <div class="pd-soru-ikon">♻️</div>
          <div class="pd-soru-bas">Bu parça tanındı</div>
          <div class="pd-soru-ad">{{ taninanKart()!.veri?.parcaAdi }}</div>
          <div class="pd-soru-not">{{ ozet }} · daha önce kaydedilmiş</div>
          <button class="pd-btn pd-ana pd-blok" (click)="taninaniSec()">Takibe Devam Et →</button>
          <button class="pd-btn pd-blok pd-link" (click)="taninanKart.set(null)">Yeni kayıt olarak başlat</button>
        } @else {
          <div class="pd-soru-ikon">📦</div>
          <div class="pd-soru-bas">Yeni Parça</div>
          <div class="pd-soru-not">{{ ozet }}</div>
          @if (teorik()) {
            <div class="pd-oto">🔎 Otomatik (sahneden): @if (malzeme()) { <b>{{ malzeme() }}</b> } <b>~{{ teorik() }} kg</b> <small>(düzenlenebilir)</small></div>
          }
          <label class="pd-etk">Malzeme</label>
          <div class="pd-malz-gir">
            <input class="pd-inp" list="pd-malz-list" [ngModel]="malzeme()" (ngModelChange)="malzemeDegis($event)" placeholder="Seç ya da yaz"/>
            <div class="pd-yog"><input class="pd-inp-k" type="number" [ngModel]="yogunluk()" (ngModelChange)="yogunlukDegis($event)" placeholder="—"/><small>g/cm³</small></div>
          </div>
          <label class="pd-etk">Ölçü (mm)</label>
          <div class="pd-boyut-gir">
            <input class="pd-inp-k" type="number" [(ngModel)]="boyutX" placeholder="X"/><span>×</span>
            <input class="pd-inp-k" type="number" [(ngModel)]="boyutY" placeholder="Y"/><span>×</span>
            <input class="pd-inp-k" type="number" [(ngModel)]="boyutZ" placeholder="Z"/>
          </div>
          <label class="pd-etk">Parça Adı</label>
          <input class="pd-inp" [(ngModel)]="ad" placeholder="Örn: Mil-A1 Rev2"/>
          <div class="pd-2li">
            <div><label class="pd-etk">Müşteri</label><input class="pd-inp" [ngModel]="musteriAdi()" (ngModelChange)="musteriAdi.set($event)" placeholder="Cari adı"/></div>
            <div><label class="pd-etk">Müşteri Tel</label><input class="pd-inp" [ngModel]="musteriTel()" (ngModelChange)="musteriTel.set($event)" placeholder="05xx…"/></div>
          </div>
          <div class="pd-2li">
            <div><label class="pd-etk">Hedef Teslim</label><input class="pd-inp" type="date" [ngModel]="hedefTarih()" (ngModelChange)="hedefTarih.set($event)"/></div>
            <div><label class="pd-etk">Adet</label><input class="pd-inp" type="number" [ngModel]="adet()" (ngModelChange)="adet.set($event)" placeholder="1"/></div>
          </div>
          <label class="pd-acil"><input type="checkbox" [ngModel]="oncelik()==='acil'" (ngModelChange)="oncelik.set($event ? 'acil' : 'normal')"/> 🔴 Acil öncelik</label>
          <label class="pd-etk">Başlangıç aşaması</label>
          <div class="pd-soru-secim">
            @for (a of baslangic; track a) {
              <button class="pd-secim-kart" [style.--c]="ASAMA_BILGI[a].renk" (click)="olustur(a)">
                <span class="pd-secim-ik">{{ ASAMA_BILGI[a].simge }}</span><span>{{ a }}</span>
              </button>
            }
          </div>
        }
      </div>
    }

    <!-- ═══ TAKİP ═══ -->
    @if (parca(); as p) {
      <!-- BAŞLIK -->
      <div class="pd-head">
        <div class="pd-head-ad" [title]="p.veri?.parcaAdi">@if (oncelik()==='acil') { <span title="Acil">🔴</span> }{{ p.veri?.parcaAdi }}@if (salt()) { <span class="pd-salt-rozet" title="Salt okunur yetki">👁</span> }</div>
        <button class="pd-wa pd-link-btn" title="Müşteri durum linki + QR üret" (click)="musteriLinkiUret()">🔗</button>
        <button class="pd-wa pd-pdf-btn" title="PDF iş emri indir" (click)="pdfIndir()">📄</button>
        <button class="pd-wa" title="WhatsApp ile durum gönder" (click)="waPaylas()">📲</button>
        <div class="pd-head-rozet" [style.background]="'rgba('+ab(p).rgb+',.18)'" [style.color]="ab(p).renk" [style.border-color]="'rgba('+ab(p).rgb+',.5)'">{{ ab(p).simge }} {{ asama(p) }}</div>
      </div>
      @if (p.veri?.malzeme || p.veri?.revizyon || adet()) { <div class="pd-head-alt">{{ p.veri?.malzeme }}@if (p.veri?.revizyon) { · Rev {{ p.veri?.revizyon }} }@if (adet()) { · {{ adet() }} adet }@if (musteriAdi()) { · 🏷️ {{ musteriAdi() }} }</div> }

      <!-- SEKME BARI -->
      <div class="pd-sekmeler">
        <button class="pd-sekme" [class.aktif]="sekme()==='durum'" (click)="sekme.set('durum')">📋 Durum</button>
        <button class="pd-sekme" [class.aktif]="sekme()==='olcu'" (click)="sekme.set('olcu')">⚖️ Ölçü</button>
        <button class="pd-sekme" [class.aktif]="sekme()==='finans'" (click)="sekme.set('finans')">💰 Finans</button>
        <button class="pd-sekme" [class.aktif]="sekme()==='hareket'" (click)="sekme.set('hareket')">🗂️ İşlem <span class="pd-sekme-rozet">{{ alt().length }}</span></button>
      </div>

      @switch (sekme()) {
        <!-- ═══ DURUM ═══ -->
        @case ('durum') {
          <div class="pd-sekme-ic">
            <!-- AŞAMA STEPPER -->
            <div class="pd-bolum-bas">Üretim Akışı</div>
            <div class="pd-stepper">
              @for (a of PARCA_ASAMALAR; track a; let i = $index) {
                <button class="pd-step" [class.aktif]="asama(p)===a" [class.gecti]="ai(asama(p))>i" [style.--c]="ASAMA_BILGI[a].renk" [disabled]="salt()" (click)="asamaIlerlet(a)">
                  <span class="pd-step-yuv">{{ ai(asama(p))>i ? '✓' : ASAMA_BILGI[a].simge }}</span>
                  <span class="pd-step-et">{{ a }}</span>
                </button>
                @if (i < PARCA_ASAMALAR.length-1) { <span class="pd-step-cizgi" [class.dolu]="ai(asama(p))>i"></span> }
              }
            </div>
            @if (asamaSuresi(); as s) { <div class="pd-sure">⏱️ Bu aşamada <b>{{ s.mevcut }}</b> gün · Toplam <b>{{ s.toplam }}</b> gün</div> }

            <!-- MÜŞTERİ -->
            <div class="pd-bolum-bas">Müşteri & Termin</div>
            <div class="pd-musteri">
              <span class="pd-musteri-ik">🏷️</span>
              <input class="pd-inp-k pd-musteri-ad" [ngModel]="musteriAdi()" (ngModelChange)="musteriAdi.set($event)" (change)="ozellikKaydet()" placeholder="Müşteri / cari"/>
              <input class="pd-inp-k pd-musteri-tel" [ngModel]="musteriTel()" (ngModelChange)="musteriTel.set($event)" (change)="ozellikKaydet()" placeholder="05xx…"/>
            </div>
            <div class="pd-tarih-satir">
              <span class="pd-olcu-et">Hedef</span>
              <input class="pd-inp-k pd-tarih-inp" type="date" [ngModel]="hedefTarih()" (ngModelChange)="hedefTarih.set($event); ozellikKaydet()"/>
              @if (gecikme(); as g) { <span class="pd-gecikme" [class.gecti]="g.gecti">{{ g.metin }}</span> }
              <button class="pd-acil-btn" [class.aktif]="oncelik()==='acil'" (click)="acilTogle()" title="Acil öncelik">🔴 Acil</button>
            </div>

            <!-- MÜŞTERİ LİNKİ + QR -->
            @if (musteriUrl()) {
              <div class="pd-link-kutu"><input class="pd-inp-k pd-link-inp" readonly [value]="musteriUrl()" (focus)="$any($event.target).select()"/><button class="pd-mini" title="Kopyala" (click)="linkKopyala()">📋</button><button class="pd-mini pd-mini-ok" title="WhatsApp'tan gönder" (click)="waPaylas()">📲</button></div>
              @if (qrUrl()) { <div class="pd-qr"><img [src]="qrUrl()" alt="QR" class="pd-qr-img"/><div class="pd-qr-yan"><span>📱 Müşteri okutsun</span><button class="pd-mini" (click)="qrIndir()">⬇️ QR indir</button></div></div> }
            }
          </div>
        }

        <!-- ═══ ÖLÇÜ & AĞIRLIK ═══ -->
        @case ('olcu') {
          <div class="pd-sekme-ic">
            <div class="pd-malz-satir">
              <span class="pd-olcu-et">Malzeme</span>
              <input class="pd-inp-k pd-malz-sel" list="pd-malz-list" [ngModel]="malzeme()" (ngModelChange)="malzemeDegis($event)" (change)="malzemeKaydet()" (keyup.enter)="malzemeKaydet()" placeholder="Seç ya da yaz"/>
              <div class="pd-yog"><input class="pd-inp-k pd-yog-inp" type="number" [ngModel]="yogunluk()" (ngModelChange)="yogunlukDegis($event)" (change)="malzemeKaydet()" (keyup.enter)="malzemeKaydet()" placeholder="—"/><small>g/cm³</small></div>
            </div>
            <div class="pd-malz-satir">
              <span class="pd-olcu-et">Ölçü</span>
              <input class="pd-inp-k pd-boyut-k" type="number" [(ngModel)]="boyutX" placeholder="X"/><span class="pd-crp">×</span>
              <input class="pd-inp-k pd-boyut-k" type="number" [(ngModel)]="boyutY" placeholder="Y"/><span class="pd-crp">×</span>
              <input class="pd-inp-k pd-boyut-k" type="number" [(ngModel)]="boyutZ" placeholder="Z"/><small>mm</small>
              <button class="pd-mini pd-mini-ok" title="Ölçüyü kaydet" (click)="boyutKaydet()">✓</button>
            </div>
            <div class="pd-olcu">
              <div class="pd-olcu-kutu"><span class="pd-olcu-et">Teorik</span><span class="pd-olcu-deg">{{ teorik() || p.veri?.teorikAgirlik || '—' }} <small>kg</small></span></div>
              <div class="pd-olcu-kutu"><span class="pd-olcu-et">Ölçülen</span><div class="pd-olcu-gir"><input class="pd-inp-k" type="number" [(ngModel)]="olcKg" placeholder="—" (keyup.enter)="olcKaydet()"/><small>kg</small><button class="pd-mini" title="Kaydet" (click)="olcKaydet()">✓</button></div></div>
            </div>
          </div>
        }

        <!-- ═══ FİNANS ═══ -->
        @case ('finans') {
          <div class="pd-sekme-ic">
            @if (fin().teklif) {
              <div class="pd-fin">
                <span class="pd-fin-k"><small>Teklif</small><b>{{ fin().teklif }} ₺</b></span>
                <span class="pd-fin-k"><small>Tahsil</small><b style="color:#86efac">{{ fin().tahsil }} ₺</b></span>
                <span class="pd-fin-k pd-fin-kalan" [class.odendi]="fin().kalan<=0"><small>Kalan</small><b>{{ fin().kalan }} ₺</b></span>
              </div>
            } @else { <div class="pd-ipucu">💡 Teklif eklemek için <b>İşlem</b> sekmesi → + Ekle → Teklif.</div> }
            <div class="pd-bolum-bas">Maliyet & Kâr</div>
            <div class="pd-mal-gir">
              <label class="pd-mal-l">Malzeme ₺/kg<input class="pd-inp-k" type="number" [ngModel]="malzemeFiyat()" (ngModelChange)="malzemeFiyat.set($event)" (change)="maliyetKaydet()" placeholder="—"/></label>
              <label class="pd-mal-l">İşçilik+gider ₺<input class="pd-inp-k" type="number" [ngModel]="iscilik()" (ngModelChange)="iscilik.set($event)" (change)="maliyetKaydet()" placeholder="—"/></label>
            </div>
            @if (maliyet() > 0) {
              <div class="pd-fin">
                <span class="pd-fin-k"><small>Maliyet</small><b>{{ maliyet() }} ₺</b></span>
                @if (fin().teklif) {
                  <span class="pd-fin-k"><small>Kâr</small><b [style.color]="kar()>=0 ? '#86efac' : '#f87171'">{{ kar() }} ₺</b></span>
                  <span class="pd-fin-k"><small>Marj</small><b [style.color]="kar()>=0 ? '#86efac' : '#f87171'">%{{ marj() }}</b></span>
                }
              </div>
            }
          </div>
        }

        <!-- ═══ HAREKETLER ═══ -->
        @case ('hareket') {
          <div class="pd-sekme-ic">
            @if (salt()) { <div class="pd-ipucu">👁 Salt okunur yetki — yalnızca görüntüleyebilirsin.</div> }
            @else {
            <div class="pd-hr-bas">
              <div class="pd-ekle-grup">
                <button class="pd-ekle-btn" [class.aktif]="notModu()" (click)="notModu.set(!notModu())" title="3D'de bir noktaya tıklayıp not bırak">📍 3D Not</button>
                <button class="pd-ekle-btn" [disabled]="fotoYukleniyor()" (click)="fotoInput.click()" title="Üretim fotoğrafı ekle">{{ fotoYukleniyor() ? '⏳' : '📷' }} Foto</button>
                <input #fotoInput type="file" accept="image/*" hidden (change)="fotoSec($event)"/>
                <button class="pd-ekle-btn" [disabled]="fotoYukleniyor()" (click)="dosyaInput.click()" title="STP/dosya ekle">📎 Dosya</button>
                <input #dosyaInput type="file" accept=".stp,.step,.pdf,.dxf,.dwg,.zip,.rar" hidden (change)="dosyaSec($event)"/>
                <button class="pd-ekle-btn" (click)="ekleMenu.set(!ekleMenu())">+ Ekle</button>
              </div>
            </div>
            }
            @if (ekleMenu()) {
              <div class="pd-ekle-menu">
                @for (t of eklenebilir; track t) { <button class="pd-ekle-tip" [style.--c]="ALT_TIP_BILGI[t].renk" (click)="aksBaslat(t)">{{ ALT_TIP_BILGI[t].simge }} {{ ALT_TIP_BILGI[t].baslik }}</button> }
              </div>
            }
            @if (aks) {
              <div class="pd-aks-form">
                <span class="pd-aks-ik">{{ ALT_TIP_BILGI[aks].simge }}</span>
                <input class="pd-inp" [(ngModel)]="aksDeger" [placeholder]="aksPh()" (keyup.enter)="aksKaydet()"/>
                <button class="pd-mini pd-mini-ok" (click)="aksKaydet()">✓</button>
                <button class="pd-mini" (click)="aks=''">✕</button>
              </div>
            }
            @if (mevcutTipler().length > 1) {
              <div class="pd-hr-filtre">
                <button class="pd-hf" [class.aktif]="hareketFiltre()===''" (click)="hareketFiltre.set('')">Tümü</button>
                @for (t of mevcutTipler(); track t) {
                  <button class="pd-hf" [class.aktif]="hareketFiltre()===t" [style.--c]="ALT_TIP_BILGI[t].renk" (click)="hareketFiltre.set(t)">{{ ALT_TIP_BILGI[t].simge }} {{ ALT_TIP_BILGI[t].baslik }}</button>
                }
              </div>
            }
            <div class="pd-hr-liste">
              @for (a of filtreliAlt(); track a._id) {
                <div class="pd-hr" [style.border-left-color]="altBilgi(a).renk">
                  <span class="pd-hr-ik" [style.background]="'rgba('+'255,255,255'+',.04)'">{{ altBilgi(a).simge }}</span>
                  <div class="pd-hr-govde">
                    <div class="pd-hr-ust"><span class="pd-hr-tip" [style.color]="altBilgi(a).renk">{{ altBilgi(a).baslik }}</span><span class="pd-hr-zaman">@if (ekleyen(a)) { <span class="pd-hr-kisi">👤 {{ ekleyen(a) }}</span> · }{{ zaman(a) }}</span></div>
                    @if (duzenlenen()===a._id) {
                      <div class="pd-hr-duz"><input class="pd-inp-k pd-inp-duz" [(ngModel)]="duzDeger" (keyup.enter)="duzKaydet(a)"/><button class="pd-mini pd-mini-ok" (click)="duzKaydet(a)">✓</button><button class="pd-mini" (click)="duzenlenen.set(null)">✕</button></div>
                    } @else if (fotoMu(a)) {
                      <img class="pd-foto-thumb" [src]="fotoSrc(a)" alt="foto" (click)="fotoBuyut(fotoSrc(a, '1024x1024'))"/>
                    } @else if (dosyaMu(a)) {
                      <a class="pd-dosya-link" [href]="dosyaSrc(a)" target="_blank" rel="noopener">📎 {{ dosyaAd(a) }} ⬇️</a>
                    } @else {
                      <div class="pd-hr-oz">{{ altOzet(a) }}</div>
                    }
                  </div>
                  @if (!salt()) {
                    <div class="pd-hr-aks">
                      @if (duzenlenebilirMi(a)) { <button class="pd-hr-ikon" title="Düzenle" (click)="duzBaslat(a)">✏️</button> }
                      <button class="pd-hr-ikon pd-sil" title="Sil" (click)="altSil(a)">🗑</button>
                    </div>
                  }
                </div>
              }
              @if (!alt().length) { <div class="pd-bos">Henüz hareket yok. <b>+ Ekle</b> veya <b>📍 3D Not</b> ile başla.</div> }
            </div>
          </div>
        }
      }
    }
  </div>
  `,
  styles: [`
    .pd{display:flex;flex-direction:column;gap:0;width:344px;color:#dbe4f0;font:500 12px/1.45 system-ui,sans-serif;padding:2px;}
    /* SORU */
    .pd-soru{display:flex;flex-direction:column;align-items:stretch;text-align:center;padding:14px 12px 8px;gap:4px;}
    .pd-soru-ikon{font-size:34px;}
    .pd-soru-bas{font-size:16px;font-weight:800;}
    .pd-soru-ad{font-size:14px;font-weight:700;color:#7dd3fc;}
    .pd-soru-not{font-size:11px;color:#94a3b8;margin-bottom:6px;}
    .pd-soru-secim{display:flex;gap:6px;margin-top:2px;}
    .pd-secim-kart{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:10px 4px;background:rgba(15,20,30,.6);border:1.5px solid color-mix(in srgb,var(--c) 45%,transparent);border-radius:9px;color:#e2e8f0;font-size:11px;font-weight:600;cursor:pointer;transition:.15s;}
    .pd-secim-kart:hover{background:color-mix(in srgb,var(--c) 18%,transparent);transform:translateY(-2px);}
    .pd-secim-ik{font-size:20px;}
    .pd-etk{font-size:10px;color:#9fb4cc;text-align:left;margin:6px 0 2px;font-weight:600;}
    .pd-oto{font-size:12px;color:#7dd3fc;background:rgba(56,189,248,.1);border:1px solid rgba(56,189,248,.25);border-radius:8px;padding:7px 10px;margin:6px 0;display:flex;gap:8px;flex-wrap:wrap;align-items:center;justify-content:center;}
    .pd-oto b{color:#e2f4ff;}
    .pd-malz-gir{display:flex;align-items:center;gap:6px;}
    .pd-malz-gir .pd-inp{flex:1;}
    .pd-yog{display:flex;align-items:center;gap:3px;}.pd-yog small{font-size:9px;color:#9fb4cc;}
    .pd-yog-inp{width:52px;}
    .pd-2li{display:flex;gap:8px;}.pd-2li>div{flex:1;}
    .pd-acil{display:flex;align-items:center;gap:6px;font-size:11px;color:#fca5a5;margin:8px 0 2px;cursor:pointer;}
    .pd-acil input{cursor:pointer;}
    .pd-musteri{display:flex;align-items:center;gap:6px;margin:6px 4px 2px;}
    .pd-musteri-ik{font-size:13px;}
    .pd-musteri-ad{flex:1;width:auto;}.pd-musteri-tel{flex:0 0 110px;width:auto;}
    .pd-tarih-satir{display:flex;align-items:center;gap:6px;margin:6px 4px 2px;}
    .pd-tarih-inp{flex:0 0 auto;width:auto;color-scheme:dark;}
    .pd-gecikme{font-size:10px;font-weight:700;color:#fbbf24;}.pd-gecikme.gecti{color:#f87171;}
    .pd-acil-btn{margin-left:auto;background:rgba(120,30,30,.4);border:1px solid rgba(248,113,113,.4);color:#fca5a5;border-radius:6px;padding:3px 8px;font-size:10px;font-weight:700;cursor:pointer;}
    .pd-acil-btn.aktif{background:#dc2626;border-color:#ef4444;color:#fff;box-shadow:0 0 8px rgba(239,68,68,.5);}
    .pd-boyut-gir{display:flex;align-items:center;gap:4px;}.pd-boyut-gir span{color:#9fb4cc;flex:0 0 auto;}.pd-boyut-gir .pd-inp-k{flex:1;width:0;min-width:0;text-align:center;}
    .pd-boyut-k{flex:1;width:0;min-width:0;text-align:center;}.pd-crp{color:#9fb4cc;flex:0 0 auto;}
    .pd-malz-satir{display:flex;align-items:center;gap:8px;margin:2px 4px 6px;}
    .pd-malz-sel{flex:1;width:auto;}
    /* BAŞLIK */
    .pd-head{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 4px 2px;}
    .pd-head-ad{font-size:16px;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .pd-head-rozet{flex-shrink:0;padding:3px 10px;border-radius:6px;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;border:1px solid;}
    .pd-head-alt{font-size:11px;color:#94a3b8;padding:0 4px 6px;}
    .pd-sekmeler{display:flex;gap:3px;margin:4px 0 8px;border-bottom:1px solid rgba(255,255,255,.08);}
    .pd-sekme{flex:1;background:none;border:none;border-bottom:2px solid transparent;color:#9fb4cc;padding:7px 4px;font-size:11px;font-weight:700;cursor:pointer;transition:.15s;white-space:nowrap;}
    .pd-sekme:hover{color:#e2e8f0;}
    .pd-sekme.aktif{color:#7dd3fc;border-bottom-color:#38bdf8;}
    .pd-sekme-rozet{display:inline-block;min-width:16px;background:rgba(56,189,248,.2);color:#7dd3fc;border-radius:8px;padding:0 4px;font-size:9px;}
    .pd-sekme-ic{animation:pdFade .18s ease;min-height:120px;}
    @keyframes pdFade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
    .pd-ipucu{font-size:11px;color:#9fb4cc;background:rgba(56,189,248,.08);border:1px solid rgba(56,189,248,.2);border-radius:8px;padding:8px 10px;margin:2px 4px 8px;}
    .pd-salt-rozet{font-size:12px;margin-left:6px;opacity:.8;}
    .pd-wa{background:#25d366;border:none;border-radius:7px;width:30px;height:26px;font-size:15px;cursor:pointer;flex-shrink:0;line-height:1;transition:.15s;}
    .pd-wa:hover{filter:brightness(1.12);transform:scale(1.06);}
    .pd-link-btn{background:#3b82f6;}
    .pd-pdf-btn{background:#475569;}
    .pd-qr{display:flex;align-items:center;gap:12px;margin:6px 0;padding:8px;background:#fff;border-radius:8px;}
    .pd-qr-img{width:96px;height:96px;border-radius:4px;}
    .pd-qr-yan{display:flex;flex-direction:column;gap:6px;}
    .pd-qr-yan span{font-size:11px;color:#0f172a;font-weight:600;}
    .pd-link-kutu{display:flex;align-items:center;gap:4px;margin:2px 0 6px;}
    .pd-link-inp{flex:1;font-size:10px;color:#7dd3fc;}
    .pd-fin{display:flex;gap:8px;margin:2px 0 2px;}
    .pd-fin-k{flex:1;display:flex;flex-direction:column;gap:1px;background:rgba(15,20,30,.5);border:1px solid rgba(255,255,255,.06);border-radius:8px;padding:6px 9px;}
    .pd-fin-k small{font-size:9px;color:#9fb4cc;text-transform:uppercase;letter-spacing:.05em;}
    .pd-fin-k b{font-size:14px;font-weight:800;color:#e2e8f0;}
    .pd-fin-kalan b{color:#fbbf24;} .pd-fin-kalan.odendi b{color:#86efac;}
    .pd-mal-gir{display:flex;gap:8px;margin:2px 4px 4px;}
    .pd-mal-l{flex:1;display:flex;flex-direction:column;gap:2px;font-size:9px;color:#9fb4cc;text-transform:uppercase;letter-spacing:.04em;}
    .pd-mal-l .pd-inp-k{width:100%;}
    /* BÖLÜM */
    .pd-bolum-bas{font-size:10px;font-weight:800;color:#9fb4cc;text-transform:uppercase;letter-spacing:.08em;margin:10px 4px 6px;border-bottom:1px solid rgba(255,255,255,.06);padding-bottom:4px;display:flex;align-items:center;justify-content:space-between;}
    /* STEPPER */
    .pd-stepper{display:flex;align-items:flex-start;padding:0 2px;}
    .pd-step{display:flex;flex-direction:column;align-items:center;gap:3px;background:none;border:none;cursor:pointer;padding:0;width:30px;flex-shrink:0;}
    .pd-step-yuv{width:26px;height:26px;display:flex;align-items:center;justify-content:center;border-radius:50%;border:1.5px solid rgba(255,255,255,.15);font-size:12px;background:#0c0e14;transition:.15s;}
    .pd-step-et{font-size:8px;color:#9fb4cc;white-space:nowrap;transform:scale(.92);}
    .pd-step.gecti .pd-step-yuv{border-color:var(--c);color:var(--c);}
    .pd-step.aktif .pd-step-yuv{border-color:var(--c);background:var(--c);color:#06121f;box-shadow:0 0 12px -2px var(--c);transform:scale(1.12);}
    .pd-step.aktif .pd-step-et{color:#e2e8f0;font-weight:700;}
    .pd-step:hover .pd-step-yuv{border-color:var(--c);}
    .pd-step-cizgi{flex:1;height:2px;background:rgba(255,255,255,.1);margin-top:13px;border-radius:2px;}
    .pd-step-cizgi.dolu{background:linear-gradient(90deg,#3b82f6,#22c55e);}
    .pd-sure{font-size:10px;color:#9fb4cc;margin:6px 4px 0;}.pd-sure b{color:#cbd5e1;}
    /* ÖLÇÜ */
    .pd-olcu{display:flex;gap:8px;}
    .pd-olcu-kutu{flex:1;background:rgba(15,20,30,.5);border:1px solid rgba(255,255,255,.06);border-radius:8px;padding:7px 10px;display:flex;flex-direction:column;gap:2px;}
    .pd-olcu-et{font-size:9px;color:#9fb4cc;text-transform:uppercase;letter-spacing:.05em;font-weight:600;}
    .pd-olcu-deg{font-size:15px;font-weight:800;color:#9fe6ff;}.pd-olcu-deg small{font-size:10px;color:#9fb4cc;}
    .pd-olcu-gir{display:flex;align-items:center;gap:4px;}.pd-olcu-gir small{font-size:10px;color:#9fb4cc;}
    /* HAREKETLER */
    .pd-hr-bas{align-items:center;}
    .pd-sayac{display:inline-block;min-width:18px;text-align:center;background:rgba(56,189,248,.18);color:#7dd3fc;border-radius:9px;padding:0 5px;font-size:10px;margin-left:4px;}
    .pd-ekle-grup{display:flex;gap:4px;}
    .pd-ekle-btn{background:rgba(56,189,248,.12);border:1px solid rgba(56,189,248,.3);color:#7dd3fc;border-radius:6px;padding:3px 9px;font-size:10px;font-weight:700;cursor:pointer;text-transform:none;letter-spacing:0;transition:.15s;}
    .pd-ekle-btn:hover{background:rgba(56,189,248,.22);}
    .pd-ekle-btn.aktif{background:#0ea5e9;color:#06121f;border-color:#0ea5e9;}
    .pd-ekle-menu{display:flex;flex-wrap:wrap;gap:4px;margin:0 4px 6px;padding:6px;background:rgba(10,15,25,.6);border-radius:8px;}
    .pd-ekle-tip{background:rgba(15,20,30,.6);border:1px solid color-mix(in srgb,var(--c) 40%,transparent);color:#e2e8f0;border-radius:6px;padding:5px 10px;font-size:11px;font-weight:600;cursor:pointer;}
    .pd-ekle-tip:hover{background:color-mix(in srgb,var(--c) 18%,transparent);}
    .pd-aks-form{display:flex;align-items:center;gap:5px;margin:0 4px 6px;}
    .pd-aks-ik{font-size:15px;}
    .pd-hr-filtre{display:flex;flex-wrap:wrap;gap:4px;margin:0 2px 6px;}
    .pd-hf{background:rgba(15,20,30,.6);border:1px solid rgba(255,255,255,.12);color:#9fb4cc;border-radius:14px;padding:3px 9px;font-size:10px;font-weight:600;cursor:pointer;transition:.15s;}
    .pd-hf:hover{filter:brightness(1.2);}
    .pd-hf.aktif{background:color-mix(in srgb,var(--c,#38bdf8) 25%,transparent);border-color:var(--c,#38bdf8);color:#fff;}
    .pd-hr-liste{display:flex;flex-direction:column;gap:5px;max-height:230px;overflow-y:auto;padding:0 2px 2px;}
    .pd-hr{display:flex;align-items:flex-start;gap:8px;background:rgba(15,20,30,.45);border:1px solid rgba(255,255,255,.05);border-left:3px solid #38bdf8;border-radius:7px;padding:7px 8px;}
    .pd-hr-ik{flex-shrink:0;width:24px;height:24px;display:flex;align-items:center;justify-content:center;border-radius:6px;font-size:13px;}
    .pd-hr-govde{flex:1;min-width:0;}
    .pd-hr-ust{display:flex;align-items:baseline;justify-content:space-between;gap:6px;}
    .pd-hr-tip{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;}
    .pd-hr-zaman{font-size:9px;color:#9fb4cc;flex-shrink:0;}
    .pd-hr-kisi{color:#7dd3fc;}
    .pd-hr-oz{font-size:12px;color:#cbd5e1;word-break:break-word;margin-top:1px;}
    .pd-dosya-link{display:inline-flex;align-items:center;gap:5px;font-size:12px;color:#7dd3fc;text-decoration:none;margin-top:3px;word-break:break-all;}
    .pd-dosya-link:hover{text-decoration:underline;}
    .pd-foto-thumb{width:100%;max-width:160px;height:90px;object-fit:cover;border-radius:6px;margin-top:4px;cursor:pointer;border:1px solid rgba(255,255,255,.1);}
    .pd-foto-thumb:hover{filter:brightness(1.1);}
    .pd-foto-katman{position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:3000;display:flex;align-items:center;justify-content:center;cursor:zoom-out;}
    .pd-foto-katman img{max-width:92vw;max-height:92vh;border-radius:8px;}
    .pd-ekle-btn[disabled]{opacity:.5;cursor:wait;}
    .pd-hr-duz{display:flex;align-items:center;gap:4px;margin-top:3px;}
    .pd-inp-duz{flex:1;}
    .pd-hr-aks{display:flex;flex-direction:column;gap:2px;flex-shrink:0;}
    .pd-hr-ikon{background:none;border:none;cursor:pointer;font-size:12px;opacity:.55;padding:1px 3px;border-radius:4px;transition:.12s;}
    .pd-hr-ikon:hover{opacity:1;background:rgba(255,255,255,.08);}
    .pd-sil:hover{background:rgba(239,68,68,.2);}
    .pd-bos{text-align:center;color:#9fb4cc;font-size:11px;padding:14px 8px;}
    /* ORTAK */
    .pd-btn{border:1px solid rgba(255,255,255,.16);color:#dbe4f0;background:rgba(15,20,30,.7);border-radius:7px;padding:8px 12px;font-size:12px;font-weight:600;cursor:pointer;transition:.15s;}
    .pd-btn:hover{filter:brightness(1.2);}
    .pd-ana{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#3b82f6;color:#fff;}
    .pd-blok{width:100%;margin-top:6px;}
    .pd-link{background:none;border:none;color:#7dd3fc;font-size:11px;padding:4px;}
    .pd-inp{flex:1;width:100%;background:rgba(10,16,28,.7);border:1px solid rgba(255,255,255,.15);border-radius:6px;padding:7px 9px;color:#fff;outline:none;font-size:12px;}
    .pd-inp:focus{border-color:#38bdf8;}
    .pd-inp-k{width:64px;background:rgba(10,16,28,.7);border:1px solid rgba(255,255,255,.15);border-radius:5px;padding:3px 6px;color:#fff;outline:none;font-size:12px;}
    .pd-mini{background:rgba(15,20,30,.8);border:1px solid rgba(255,255,255,.15);color:#cbd5e1;border-radius:5px;padding:2px 7px;font-size:11px;cursor:pointer;}
    .pd-mini:hover{filter:brightness(1.3);}
    .pd-mini-ok{background:rgba(34,197,94,.2);border-color:rgba(34,197,94,.5);color:#86efac;}
  `],
})
export class FikoParcaTakipDialog implements OnDestroy {
  @Input() fingerprint?: IParcaFingerprint;
  @Input() parcaAd = '';
  @Input() taninan: IKart | null = null;
  @Input() sahne: any;
  @Input() pencere: any;
  @Input() onAgirlik?: number;          // sahneden otomatik teorik ağırlık (kg)
  @Input() onBoyut?: { x: number; y: number; z: number };   // sahneden otomatik ölçü (mm)
  @Input() onMalzeme?: string;          // modelleme yöneticisinden seçili malzeme
  @Input() onHacimCm3?: number;         // gövde hacmi (cm³) — malzeme değişince ağırlık yeniden hesabı
  @Input() onMalzemeler?: { ad: string; yogunluk: number }[];   // malzeme + yoğunluk listesi

  private svc = inject(ParcaTakipService);
  private fy = inject(FikoYTService);
  cd = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  readonly ASAMA_BILGI = ASAMA_BILGI;
  readonly PARCA_ASAMALAR = PARCA_ASAMALAR;
  readonly ALT_TIP_BILGI = ALT_TIP_BILGI;
  readonly baslangic: ParcaAsama[] = ['Teklif', 'Onay', 'Üretim'];
  readonly eklenebilir: ParcaAltTip[] = ['NOT', 'OLCU', 'AGIRLIK', 'TEKLIF', 'ONAY', 'ODEME'];

  parca = signal<IKart | null>(null);
  taninanKart = signal<IKart | null>(null);
  alt = signal<IKart[]>([]);
  malzeme = signal<string>('');         // seçili/yazılan malzeme (oluşturma + takip)
  yogunluk = signal<number | null>(null); // g/cm³ (listeden otomatik veya elle)
  teorik = signal<number | null>(null); // güncel teorik ağırlık (kg)
  boyutX: number | null = null; boyutY: number | null = null; boyutZ: number | null = null;   // düzenlenebilir ölçü (mm)
  hedefTarih = signal<string>('');           // hedef teslim tarihi
  oncelik = signal<'normal' | 'acil'>('normal');
  adet = signal<number | null>(null);        // sipariş adedi
  malzemeFiyat = signal<number | null>(null); // ₺/kg
  iscilik = signal<number | null>(null);      // ₺
  musteriAdi = signal<string>('');           // cari adı
  musteriTel = signal<string>('');           // müşteri telefonu
  notModu = signal(false);
  ekleMenu = signal(false);
  duzenlenen = signal<string | null>(null);
  musteriUrl = signal<string>('');
  qrUrl = signal<string>('');           // müşteri linkinin QR data-url'i
  fotoYukleniyor = signal(false);
  fotoBuyuk = signal<string>('');        // lightbox
  sekme = signal<'durum' | 'olcu' | 'finans' | 'hareket'>('durum');
  salt = signal(false);   // salt-okunur (parca_takip.goruntule yetkisi)
  hareketFiltre = signal<ParcaAltTip | ''>('');   // İşlem sekmesi: tip bazlı geçmiş filtresi
  /** Filtreye göre hareketler (ölçüm/durum/… geçmişi ayrı görülebilir). */
  filtreliAlt = computed(() => { const f = this.hareketFiltre(); return f ? this.alt().filter(a => this.altTipi(a) === f) : this.alt(); });
  /** Hareketlerde mevcut tipler (filtre çubuğu için, sıralı). */
  mevcutTipler = computed(() => {
    const sira: ParcaAltTip[] = ['DURUM', 'OLCU', 'AGIRLIK', 'NOT', 'FOTO', 'TEKLIF', 'ODEME', 'ONAY'];
    const varOlan = new Set(this.alt().map(a => this.altTipi(a)));
    return sira.filter(t => varOlan.has(t));
  });
  ad = ''; olcKg: number | null = null; aks: ParcaAltTip | '' = ''; aksDeger = ''; duzDeger = '';
  ozet = '';
  private grup: THREE.Group | null = null;
  private _clickH: ((e: PointerEvent) => void) | null = null;
  private _rc = new THREE.Raycaster(); private _v2 = new THREE.Vector2();

  async ngOnInit() {
    this.ad = this.parcaAd; this.ozet = this.fingerprint ? `${this.fingerprint.faceSayisi} yüzey · ${this.fingerprint.edgeSayisi} kenar` : '';
    try { this.salt.set((await this.svc.yetkiSeviye()) === 'goruntule'); } catch { }
    this.malzeme.set(this.onMalzeme || ''); this.teorik.set(this.onAgirlik ?? null);
    this.yogunluk.set(this.baslangicYogunluk(this.onMalzeme, this.onAgirlik));
    if (this.onBoyut) { this.boyutX = this.onBoyut.x; this.boyutY = this.onBoyut.y; this.boyutZ = this.onBoyut.z; }
    if (this.sahne) { this.grup = new THREE.Group(); this.grup.name = 'ParcaNotIsaret'; this.sahne.add?.(this.grup); this.tikDinle(); }
    if (this.taninan) this.taninanKart.set(this.taninan);
    else if (this.fingerprint?.fp) { try { this.taninanKart.set(await this.svc.parcaBulFingerprint(this.fingerprint.fp)); } catch { } }
    this.cd.markForCheck();
  }

  ab(k: IKart) { return ASAMA_BILGI[this.asama(k)] || ASAMA_BILGI['Teklif']; }
  asama(k: IKart): ParcaAsama { return (k?.veri?.asama || 'Teklif') as ParcaAsama; }
  ai(a: ParcaAsama) { return PARCA_ASAMALAR.indexOf(a); }
  altBilgi(a: IKart) { const t = (a?.icerikler?.[0]?.ek as any)?.altTip as ParcaAltTip; return ALT_TIP_BILGI[t] || ALT_TIP_BILGI.NOT; }
  private altTipi(a: IKart): ParcaAltTip { return ((a?.icerikler?.[0]?.ek as any)?.altTip || 'NOT') as ParcaAltTip; }
  duzenlenebilirMi(a: IKart) { return ['NOT', 'OLCU', 'TEKLIF', 'ONAY', 'ODEME', 'AGIRLIK', 'DURUM'].includes(this.altTipi(a)); }
  altOzet(a: IKart) {
    const ek: any = a?.icerikler?.[0]?.ek || {}; const t = ek.altTip;
    if (t === 'NOT') return ek.metin || '—';
    if (t === 'OLCU') return `${ek.olcuTuru || ''} ${ek.deger ?? ''} ${ek.birim || 'mm'}`.trim();
    if (t === 'AGIRLIK') return `${ek.kg ?? '—'} kg${ek.yontem ? ' · ' + ek.yontem : ''}`;
    if (t === 'DURUM') return `${ek.simge || ''} ${ek.asama || ''}${ek.aciklama ? ' — ' + ek.aciklama : ''}`;
    if (t === 'ONAY') return `${ek.sonuc || 'Onaylandı'}${ek.onaylayan ? ' · ' + ek.onaylayan : ''}${ek.aciklama ? ' · ' + ek.aciklama : ''}`;
    if (t === 'TEKLIF') return `${ek.fiyat ?? '—'} ${ek.parabirimi || '₺'}`;
    if (t === 'ODEME') return `${ek.tutar ?? '—'} ₺ tahsil · ${ek.yontem || 'nakit'}`;
    if (t === 'MONAY') return `${ek.sonuc || ''}${ek.not ? ' — ' + ek.not : ''}`;
    return '—';
  }

  /** Finansal özet: onaylanan/son teklif − toplam tahsilat = kalan bakiye. */
  fin = () => {
    const al = this.alt();
    const teklifler = al.filter(a => this.altTipi(a) === 'TEKLIF').map(a => +((a.icerikler?.[0]?.ek as any)?.fiyat) || 0).filter(x => x > 0);
    const teklif = teklifler.length ? teklifler[0] : 0;   // en yeni teklif (liste yeni→eski)
    const tahsil = al.filter(a => this.altTipi(a) === 'ODEME').reduce((s, a) => s + (+((a.icerikler?.[0]?.ek as any)?.tutar) || 0), 0);
    return { teklif, tahsil, kalan: teklif - tahsil };
  };
  /** Müşteri public durum linki üret (token) + kopyala + QR. */
  async musteriLinkiUret() {
    const p = this.parca(); if (!p?._id) return;
    const token = await this.svc.tokenUret(p._id, (p.veri as any)?.paylasimToken);
    (p.veri as any).paylasimToken = token; (p.veri as any).tokenAktif = true;
    const url = `${location.origin}/p/${token}`;
    this.musteriUrl.set(url); this.linkKopyala();
    try { const mod: any = await import('qrcode'); const QR = mod.default || mod; if (QR?.toDataURL) { this.qrUrl.set(await QR.toDataURL(url, { width: 220, margin: 1 })); this.cd.markForCheck(); } } catch (e) { console.warn('QR üretilemedi', e); }
  }
  linkKopyala() { try { navigator.clipboard?.writeText(this.musteriUrl()); } catch { } }
  qrIndir() {
    const u = this.qrUrl(); if (!u) return;
    const a = document.createElement('a'); a.href = u; a.download = `qr-${(this.parca()?.veri?.parcaAdi || 'parca').replace(/\s+/g, '-')}.png`; a.click();
  }

  /** WhatsApp ile durum paylaş — müşteri linki varsa onu da ekler (wa.me deep link). */
  waPaylas() {
    const p = this.parca(); if (!p) return;
    const f = this.fin();
    const satir = [
      `📦 ${p.veri?.parcaAdi || 'Parça'}`,
      `Durum: ${this.ab(p).simge} ${this.asama(p)}`,
      p.veri?.teorikAgirlik ? `Ağırlık: ~${p.veri.teorikAgirlik} kg` : '',
      f.teklif ? `Fiyat: ${f.teklif} ₺${f.tahsil ? ` · Tahsil: ${f.tahsil} ₺ · Kalan: ${f.kalan} ₺` : ''}` : '',
      this.musteriUrl() ? `\n📍 Canlı takip: ${this.musteriUrl()}` : '',
    ].filter(Boolean).join('\n');
    window.open('https://wa.me/' + this.waNumara() + '?text=' + encodeURIComponent(satir), '_blank');
  }
  /** Yazdırılabilir İŞ EMRİ (Türkçe-uyumlu HTML; tarayıcıdan PDF kaydedilebilir). QR varsa gömülür. */
  pdfIndir() {
    const p = this.parca(); if (!p) return; const v: any = p.veri || {};
    const f = this.fin(); const s = this.asamaSuresi();
    const olcu = (this.boyutX != null && this.boyutY != null && this.boyutZ != null) ? `${this.boyutX}×${this.boyutY}×${this.boyutZ} mm` : '';
    const esc = (x: any) => String(x ?? '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' } as any)[c]);
    const sat = (k: string, val: any) => val ? `<tr><td class="k">${k}</td><td>${esc(val)}</td></tr>` : '';
    const w = window.open('', '_blank', 'width=820,height=1040'); if (!w) { this.cd.markForCheck(); return; }
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>İş Emri — ${esc(v.parcaAdi)}</title>
    <style>
      *{box-sizing:border-box;font-family:Segoe UI,Arial,sans-serif;}
      body{margin:0;padding:32px;color:#1a1a1a;}
      .ust{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #2563eb;padding-bottom:12px;margin-bottom:18px;}
      h1{margin:0;font-size:26px;color:#1d4ed8;} .alt{color:#666;font-size:13px;margin-top:2px;}
      .qr{width:120px;height:120px;}
      table{width:100%;border-collapse:collapse;margin:10px 0;}
      td{padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:14px;} td.k{font-weight:700;color:#444;width:38%;background:#f8fafc;}
      .rozet{display:inline-block;padding:3px 12px;border-radius:6px;font-weight:800;font-size:13px;background:#dbeafe;color:#1d4ed8;}
      .acil{background:#fee2e2;color:#dc2626;}
      .baslik{font-size:13px;font-weight:800;color:#1d4ed8;text-transform:uppercase;letter-spacing:.05em;margin:18px 0 4px;border-bottom:1px solid #e5e7eb;padding-bottom:3px;}
      .imza{margin-top:50px;display:flex;justify-content:space-between;} .imza div{width:45%;border-top:1px solid #999;padding-top:6px;text-align:center;font-size:12px;color:#666;}
      @media print{body{padding:14px;}}
    </style></head><body>
      <div class="ust">
        <div><h1>📦 İŞ EMRİ</h1><div class="alt">${esc(v.parcaAdi)}${v.revizyon ? ' · Rev ' + esc(v.revizyon) : ''}</div>
          <div style="margin-top:8px"><span class="rozet">${esc(this.asama(p))}</span> ${v.oncelik === 'acil' ? '<span class="rozet acil">🔴 ACİL</span>' : ''}</div>
        </div>
        ${this.qrUrl() ? `<img class="qr" src="${this.qrUrl()}"/>` : ''}
      </div>
      <div class="baslik">Müşteri</div>
      <table>${sat('Müşteri', v.musteriAdi)}${sat('Telefon', v.musteriTel)}${sat('Adet', v.adet)}${sat('Hedef Teslim', v.hedefTarih)}</table>
      <div class="baslik">Teknik</div>
      <table>${sat('Malzeme', v.malzeme)}${sat('Teorik Ağırlık', v.teorikAgirlik ? v.teorikAgirlik + ' kg' : '')}${sat('Ölçülen Ağırlık', v.olculenAgirlik ? v.olculenAgirlik + ' kg' : '')}${sat('Ölçü', olcu)}${s ? sat('Süre', `Bu aşamada ${s.mevcut} gün · Toplam ${s.toplam} gün`) : ''}</table>
      ${f.teklif ? `<div class="baslik">Finans</div><table>${sat('Teklif', f.teklif + ' ₺')}${sat('Tahsil', f.tahsil + ' ₺')}${sat('Kalan', f.kalan + ' ₺')}${this.maliyet() > 0 ? sat('Maliyet', this.maliyet() + ' ₺') + sat('Kâr', this.kar() + ' ₺ (%' + this.marj() + ')') : ''}</table>` : ''}
      <div class="imza"><div>Hazırlayan</div><div>Onay</div></div>
      <div style="margin-top:24px;text-align:center;color:#999;font-size:11px">FikoYT Parça Takip · ${new Date().toLocaleString('tr')}</div>
    </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch { } }, 350);
  }
  /** Müşteri telefonunu wa.me formatına çevir (TR: 0 → 90, sadece rakam). Boşsa '' (numara seçici açılır). */
  private waNumara(): string {
    let t = (this.musteriTel() || '').replace(/\D/g, ''); if (!t) return '';
    if (t.startsWith('0')) t = '90' + t.slice(1); else if (!t.startsWith('90') && t.length === 10) t = '90' + t;
    return t;
  }
  ekleyen(a: IKart): string { return (a?.icerikler?.[0]?.ek as any)?.sahipIsim || ''; }
  fotoMu(a: IKart): boolean { return this.altTipi(a) === 'FOTO'; }
  fotoSrc(a: IKart, boyut = '256x256'): string { const id = (a?.icerikler?.[0]?.ek as any)?.dosyaId; return id ? this.svc.fotoUrl(id, boyut) : ''; }
  fotoBuyut(src: string) { if (src) this.fotoBuyuk.set(src); }
  dosyaMu(a: IKart): boolean { return this.altTipi(a) === 'DOSYA'; }
  dosyaSrc(a: IKart): string { const id = (a?.icerikler?.[0]?.ek as any)?.dosyaId; return id ? this.svc.dosyaUrl(id) : ''; }
  dosyaAd(a: IKart): string { return (a?.icerikler?.[0]?.ek as any)?.ismi || 'dosya'; }
  async fotoSec(e: Event) { await this._dosyaYukle(e, 'foto'); }
  async dosyaSec(e: Event) { await this._dosyaYukle(e, 'dosya'); }
  private async _dosyaYukle(e: Event, tip: 'foto' | 'dosya') {
    const inp = e.target as HTMLInputElement; const f = inp.files?.[0]; if (!f) return; inp.value = '';
    const p = this.parca(); if (!p?._id) return;
    this.fotoYukleniyor.set(true); this.cd.markForCheck();
    try {
      const id = tip === 'foto' ? await this.svc.fotoEkle(p._id, f) : await this.svc.dosyaEkle(p._id, f);
      if (id) await this.altYenile(p._id); else console.warn('Yüklenemedi (sunucu)');
    } catch (e2) { console.warn('Yükleme hatası', e2); }
    finally { this.fotoYukleniyor.set(false); this.cd.markForCheck(); }
  }
  /** Mevcut aşamada ve toplam (oluşturmadan) geçen gün — son DURUM hareketinden / parça tarihinden. */
  asamaSuresi(): { mevcut: number; toplam: number } | null {
    const p = this.parca(); if (!p) return null;
    const durumlar = this.alt().filter(a => this.altTipi(a) === 'DURUM');
    const olusturma = (p as any).tarih ? new Date((p as any).tarih).getTime() : 0; if (!olusturma) return null;
    const sonGecis = durumlar.length && (durumlar[0] as any).tarih ? new Date((durumlar[0] as any).tarih).getTime() : olusturma;
    const gun = (ms: number) => Math.max(0, Math.floor((Date.now() - ms) / 86400000));
    return { mevcut: gun(sonGecis), toplam: gun(olusturma) };
  }
  /** Hedef tarihe göre gecikme/kalan gün (Teslim/Sevkiyat dışı parçalar için). */
  gecikme(): { metin: string; gecti: boolean } | null {
    const t = this.hedefTarih(); if (!t) return null;
    const p = this.parca(); const a = p ? this.asama(p) : null;
    if (a === 'Teslim' || a === 'Sevkiyat' || a === 'İptal' || a === 'Red') return null;
    const farkGun = Math.ceil((new Date(t + 'T23:59:59').getTime() - Date.now()) / 86400000);
    if (farkGun < 0) return { metin: `${-farkGun} gün gecikti`, gecti: true };
    if (farkGun === 0) return { metin: 'Bugün teslim', gecti: false };
    if (farkGun <= 7) return { metin: `${farkGun} gün kaldı`, gecti: false };
    return null;
  }
  async ozellikKaydet() {
    const p = this.parca(); if (!p?._id) return;
    const yeni = { ...(p.veri as IParcaVeri), hedefTarih: this.hedefTarih(), oncelik: this.oncelik(), adet: this.adet() ?? undefined, musteriAdi: this.musteriAdi(), musteriTel: this.musteriTel() };
    await this.svc.parcaGuncelle(p._id, yeni);
    const v: any = p.veri; v.hedefTarih = this.hedefTarih(); v.oncelik = this.oncelik(); v.adet = this.adet(); v.musteriAdi = this.musteriAdi(); v.musteriTel = this.musteriTel();
    this.parca.set({ ...p }); this.cd.markForCheck();
  }
  acilTogle() { this.oncelik.set(this.oncelik() === 'acil' ? 'normal' : 'acil'); this.ozellikKaydet(); }
  /** Maliyet = teorik ağırlık × ₺/kg + işçilik. */
  maliyet(): number { const ag = +(this.teorik() || 0); const bf = +(this.malzemeFiyat() || 0); const isc = +(this.iscilik() || 0); return +(ag * bf + isc).toFixed(2); }
  kar(): number { return +(this.fin().teklif - this.maliyet()).toFixed(2); }
  marj(): number { const t = this.fin().teklif; return t > 0 ? Math.round(this.kar() / t * 100) : 0; }
  async maliyetKaydet() {
    const p = this.parca(); if (!p?._id) return;
    const yeni = { ...(p.veri as IParcaVeri), malzemeBirimFiyat: this.malzemeFiyat() ?? undefined, iscilik: this.iscilik() ?? undefined };
    await this.svc.parcaGuncelle(p._id, yeni);
    (p.veri as any).malzemeBirimFiyat = this.malzemeFiyat(); (p.veri as any).iscilik = this.iscilik(); this.parca.set({ ...p }); this.cd.markForCheck();
  }
  zaman(a: IKart): string {
    const t = (a as any)?.tarih ? new Date((a as any).tarih).getTime() : 0; if (!t) return '';
    const fark = Date.now() - t;
    if (fark < 60000) return 'az önce';
    if (fark < 3600000) return Math.floor(fark / 60000) + ' dk';
    if (fark < 86400000) return Math.floor(fark / 3600000) + ' sa';
    if (fark < 604800000) return Math.floor(fark / 86400000) + ' gün';
    return new Date(t).toLocaleDateString('tr');
  }
  aksPh() { return this.aks === 'OLCU' ? 'Örn: Çap 25' : this.aks === 'AGIRLIK' ? 'kg' : this.aks === 'ONAY' ? 'Onay açıklaması' : this.aks === 'TEKLIF' ? 'Fiyat (₺)' : this.aks === 'ODEME' ? 'Tahsil edilen (₺)' : 'Not metni'; }

  async taninaniSec() { const t = this.taninanKart(); if (t?._id) await this.yukle(t._id); }
  async olustur(a: ParcaAsama) {
    const kg = this.teorik() ?? this.onAgirlik;
    const veri: IParcaVeri = { parcaAdi: this.ad || 'Parça', asama: a, fingerprint: this.fingerprint };
    if (kg) veri.teorikAgirlik = kg;                            // otomatik/seçili ağırlık
    if (this.malzeme()) veri.malzeme = this.malzeme();          // seçili malzeme
    if (this.hedefTarih()) veri.hedefTarih = this.hedefTarih(); // hedef teslim
    if (this.oncelik() === 'acil') veri.oncelik = 'acil';       // acil bayrağı
    if (this.adet()) veri.adet = this.adet()!;                  // sipariş adedi
    if (this.malzemeFiyat()) veri.malzemeBirimFiyat = this.malzemeFiyat()!;   // ₺/kg
    if (this.iscilik()) veri.iscilik = this.iscilik()!;         // işçilik+gider
    if (this.musteriAdi()) veri.musteriAdi = this.musteriAdi(); // müşteri
    if (this.musteriTel()) veri.musteriTel = this.musteriTel();
    const id = await this.svc.parcaOlustur(veri);
    if (this.boyutX != null && this.boyutY != null && this.boyutZ != null) await this.svc.altKartEkle(id, 'OLCU', { olcuTuru: 'Boyut', deger: `${this.boyutX}×${this.boyutY}×${this.boyutZ}`, birim: 'mm', otomatik: true });   // ölçü (düzenlenebilir)
    if (kg) await this.svc.altKartEkle(id, 'AGIRLIK', { kg, yontem: this.malzeme() ? 'malzeme: ' + this.malzeme() : 'otomatik' });   // ağırlık takibi (hareket)
    await this.yukle(id);
  }
  private async yukle(id: string) { const k = await this.svc.parcaGetir(id); if (k) { this.parca.set(k); const md = (k.veri as any)?.malzeme || this.onMalzeme || ''; const kg = (k.veri as any)?.teorikAgirlik ?? this.onAgirlik ?? null; this.malzeme.set(md); this.teorik.set(kg); this.yogunluk.set(this.baslangicYogunluk(md, kg)); this.hedefTarih.set((k.veri as any)?.hedefTarih || ''); this.oncelik.set((k.veri as any)?.oncelik || 'normal'); this.adet.set((k.veri as any)?.adet ?? null); this.malzemeFiyat.set((k.veri as any)?.malzemeBirimFiyat ?? null); this.iscilik.set((k.veri as any)?.iscilik ?? null); this.musteriAdi.set((k.veri as any)?.musteriAdi || ''); this.musteriTel.set((k.veri as any)?.musteriTel || ''); await this.altYenile(id); this.boyutuOku(); } this.cd.markForCheck(); }

  /** Kayıtlı Boyut (OLCU) hareketinden ölçüleri input'lara oku. */
  private boyutuOku() {
    const b = this.alt().find(a => { const ek: any = a?.icerikler?.[0]?.ek; return ek?.altTip === 'OLCU' && ek?.olcuTuru === 'Boyut'; });
    const d = (b?.icerikler?.[0]?.ek as any)?.deger;
    if (typeof d === 'string') { const pp = d.split(/[×x*]/).map(s => parseFloat(s)); if (pp.length === 3 && pp.every(n => Number.isFinite(n))) { this.boyutX = pp[0]; this.boyutY = pp[1]; this.boyutZ = pp[2]; } }
  }
  /** Ölçü kaydet → HER kayıt yeni OLCU hareketi (ölçüm geçmişi birikir; değişmediyse yazma). */
  async boyutKaydet() {
    const p = this.parca(); if (!p?._id || this.boyutX == null || this.boyutY == null || this.boyutZ == null) return;
    const deger = `${this.boyutX}×${this.boyutY}×${this.boyutZ}`;
    const son = this.alt().find(a => { const ek: any = a?.icerikler?.[0]?.ek; return ek?.altTip === 'OLCU' && ek?.olcuTuru === 'Boyut'; });
    if ((son?.icerikler?.[0]?.ek as any)?.deger === deger) return;   // aynıysa yeni kayıt açma
    await this.svc.altKartEkle(p._id, 'OLCU', { olcuTuru: 'Boyut', deger, birim: 'mm' });
    await this.altYenile(p._id); this.cd.markForCheck();
  }

  /** Başlangıç yoğunluğu: bilinen malzemeden, yoksa teorik ağırlık + hacimden türet. */
  private baslangicYogunluk(ad?: string, kg?: number | null): number | null {
    const m = this.onMalzemeler?.find(x => x.ad?.toLowerCase() === (ad || '').toLowerCase());
    if (m) return m.yogunluk;
    if (kg && this.onHacimCm3) return +(kg * 1000 / this.onHacimCm3).toFixed(3);
    return null;
  }

  /** Malzeme yaz/seç → listede varsa yoğunluğu otomatik al, ağırlığı ANINDA hesapla (DB'ye DOKUNMA — kayıt blur/Enter'da). */
  malzemeDegis(ad: string) {
    this.malzeme.set(ad || '');
    const yog = this.onMalzemeler?.find(m => m.ad?.toLowerCase() === (ad || '').toLowerCase())?.yogunluk;
    if (yog) this.yogunluk.set(yog);   // bilinen malzeme → yoğunluk otomatik
    this.agirlikHesapla(); this.cd.markForCheck();
  }
  /** Elle yoğunluk → ağırlığı anında hesapla (DB blur/Enter'da). */
  yogunlukDegis(y: any) {
    const n = parseFloat(y); this.yogunluk.set(Number.isFinite(n) && n > 0 ? n : null);
    this.agirlikHesapla(); this.cd.markForCheck();
  }
  /** Ağırlık = hacim(cm³) × yoğunluk(g/cm³) / 1000 (kg). Hacim/yoğunluk yoksa dokunma. */
  private agirlikHesapla() { const y = this.yogunluk(); if (y && this.onHacimCm3) this.teorik.set(+(this.onHacimCm3 * y / 1000).toFixed(2)); }
  /** Malzeme/teorik ağırlığı mevcut parçaya kaydet — input ODAK KAYBINDA/Enter'da bir kez (her harfte DEĞİL). AGIRLIK hareketi EKLEMEZ (teorik zaten veride; hareket gerçek tartım/başlangıç için). */
  async malzemeKaydet() {
    const p = this.parca(); if (!p?._id) return;
    const kg = this.teorik(); const oncekiKg = (p.veri as any)?.teorikAgirlik;
    if ((p.veri as any)?.malzeme === this.malzeme() && oncekiKg === kg) return;   // değişmediyse yazma
    const yeni = { ...(p.veri as IParcaVeri), malzeme: this.malzeme() }; if (kg != null) yeni.teorikAgirlik = kg;
    await this.svc.parcaGuncelle(p._id, yeni);
    (p.veri as any).malzeme = this.malzeme(); if (kg != null) (p.veri as any).teorikAgirlik = kg; this.parca.set({ ...p });
    // teorik ağırlık DEĞİŞTİYSE geçmişe AGIRLIK hareketi
    if (kg != null && kg !== oncekiKg) { await this.svc.altKartEkle(p._id, 'AGIRLIK', { kg, yontem: 'teorik' + (this.malzeme() ? ' · ' + this.malzeme() : '') }); await this.altYenile(p._id); }
    this.cd.markForCheck();
  }
  private async altYenile(id: string) {
    const a = await this.svc.altKartlar(id); this.alt.set(a);
    if (this.grup) { while (this.grup.children.length) { const o: any = this.grup.children.pop(); o.material?.map?.dispose?.(); o.material?.dispose?.(); } for (const ak of a) { const ek: any = ak?.icerikler?.[0]?.ek; if (ek?.altTip === 'NOT' && ek?.x != null) this.isaret(ek.x, ek.y, ek.z); } }
  }
  async asamaIlerlet(a: ParcaAsama) { const p = this.parca(); if (!p?._id) return; await this.svc.asamaIlerlet(p._id, a); (p.veri as any).asama = a; this.parca.set({ ...p }); await this.altYenile(p._id); }
  async olcKaydet() { const p = this.parca(); const kg = Number(this.olcKg); if (!p?._id || !Number.isFinite(kg)) return; await this.svc.parcaGuncelle(p._id, { ...(p.veri as IParcaVeri), olculenAgirlik: kg }); await this.svc.altKartEkle(p._id, 'AGIRLIK', { kg, yontem: 'ölçüm' }); (p.veri as any).olculenAgirlik = kg; this.parca.set({ ...p }); await this.altYenile(p._id); }

  aksBaslat(t: ParcaAltTip) { this.aks = t; this.aksDeger = ''; this.ekleMenu.set(false); }
  async aksKaydet() {
    const p = this.parca(); const v = this.aksDeger.trim(); if (!p?._id || (!v && this.aks !== 'ONAY')) return;
    let al: any = {};
    if (this.aks === 'NOT') al = { metin: v };
    else if (this.aks === 'OLCU') al = { olcuTuru: v.split(' ')[0] || 'Ölçü', deger: this.sayi(v), birim: 'mm' };
    else if (this.aks === 'AGIRLIK') al = { kg: this.sayi(v), yontem: 'giriş' };
    else if (this.aks === 'ONAY') al = { sonuc: 'Onaylandı', onaylayan: this.fy.kimlik?.aktifKullaniciJson?.()?.isim || '', aciklama: v };
    else if (this.aks === 'TEKLIF') al = { fiyat: this.sayi(v), parabirimi: '₺' };
    else if (this.aks === 'ODEME') al = { tutar: this.sayi(v), yontem: 'nakit' };
    await this.svc.altKartEkle(p._id, this.aks as ParcaAltTip, al, this.aks === 'NOT' ? v : ''); this.aks = ''; this.aksDeger = ''; await this.altYenile(p._id);
  }
  private sayi(v: string): number | string { const n = parseFloat(v.replace(/[^0-9.,-]/g, '').replace(',', '.')); return Number.isFinite(n) ? n : v; }

  async altSil(a: IKart) { if (!a?._id) return; await this.svc.altKartSil(a._id); this.alt.set(this.alt().filter(x => x._id !== a._id)); await this.altYenile(this.parca()!._id!); }
  duzBaslat(a: IKart) { const ek: any = a?.icerikler?.[0]?.ek || {}; const t = ek.altTip; this.duzDeger = t === 'NOT' ? ek.metin : t === 'OLCU' ? ek.deger : t === 'TEKLIF' ? ek.fiyat : t === 'ODEME' ? ek.tutar : t === 'AGIRLIK' ? ek.kg : (t === 'ONAY' || t === 'DURUM') ? ek.aciklama : ''; this.duzenlenen.set(a._id!); }
  async duzKaydet(a: IKart) {
    const t = this.altTipi(a); const v = String(this.duzDeger).trim();
    const al: any = t === 'NOT' ? { metin: v } : t === 'OLCU' ? { deger: this.sayi(v) } : t === 'TEKLIF' ? { fiyat: this.sayi(v) } : t === 'ODEME' ? { tutar: this.sayi(v) } : t === 'AGIRLIK' ? { kg: this.sayi(v) } : { aciklama: v };
    await this.svc.altKartGuncelle(a._id!, al); this.duzenlenen.set(null); await this.altYenile(this.parca()!._id!);
  }

  private tikDinle() {
    const cv: HTMLCanvasElement | undefined = this.sahne?.anaGosterim?.canvas?.nativeElement; if (!cv) return;
    this._clickH = (e) => {
      if (!this.notModu() || e.button !== 0 || !this.parca()) return;
      const kam = this.sahne?.anaGosterim?.aktifKamera; const ciz = this.sahne?.cizimler; if (!kam || !ciz) return;
      const r = cv.getBoundingClientRect();
      this._v2.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      this._rc.setFromCamera(this._v2, kam);
      const hit = this._rc.intersectObjects(ciz.children, true)[0]; if (!hit) return;
      e.stopPropagation(); e.preventDefault();
      const p = hit.point; const metin = (typeof prompt !== 'undefined') ? prompt('Not (3D nokta):') : '';
      if (!metin) return;
      const x = +p.x.toFixed(2), y = +p.y.toFixed(2), z = +p.z.toFixed(2);
      this.zone.run(async () => { const pr = this.parca(); if (!pr?._id) return; await this.svc.altKartEkle(pr._id, 'NOT', { metin, x, y, z }); this.isaret(x, y, z); await this.altYenile(pr._id); });
    };
    cv.addEventListener('pointerdown', this._clickH, true);
  }
  private isaret(x: number, y: number, z: number) {
    if (!this.grup) return;
    const cv = document.createElement('canvas'); cv.width = 128; cv.height = 128; const c = cv.getContext('2d')!;
    c.fillStyle = '#38bdf8'; c.beginPath(); c.arc(64, 64, 38, 0, 7); c.fill(); c.fillStyle = '#fff'; c.font = 'bold 60px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('📝', 64, 70);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false }));
    sp.position.set(x, y, z); sp.scale.setScalar(8); sp.renderOrder = 999; this.grup.add(sp);
    this.sahne?.anaGosterim?.ciz?.();
  }

  ngOnDestroy() {
    const cv: HTMLCanvasElement | undefined = this.sahne?.anaGosterim?.canvas?.nativeElement;
    if (cv && this._clickH) cv.removeEventListener('pointerdown', this._clickH, true);
    if (this.grup && this.sahne) this.sahne.remove?.(this.grup);
  }
}
